import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { ArrowRight } from 'lucide-react'
import Modal from '../ui/Modal'
import { api } from '../../lib/api'
import { buildPlanEconomicsDisplay, type CompareTier } from '../../lib/upgradePlanEconomics'
import { UpgradePlanCompareTable } from './UpgradePlanCompareTable'
import { UpgradePlanSocialProof } from './UpgradePlanSocialProof'

interface Props {
  open: boolean
  onClose: () => void
  currentTier: Extract<CompareTier, 'FREE' | 'STANDARD'>
  upgradePath: string
}

export function UpgradePlanCompareModal({ open, onClose, currentTier, upgradePath }: Props) {
  const { t, i18n } = useTranslation('credits')

  const { data: plans = [] } = useQuery({
    queryKey: ['creditPlans', i18n.language],
    queryFn: async () => {
      const res = await api.get('/credits/plans')
      return res.data
    },
    staleTime: 60_000,
    enabled: open,
  })

  const { data: featurePricing = [] } = useQuery<{ action_key: string; cost_points: number }[]>({
    queryKey: ['featurePricing'],
    queryFn: async () => {
      const res = await api.get('/credits/feature-pricing')
      return res.data
    },
    staleTime: 60_000,
    enabled: open,
  })

  const { economics, offerPointCost } = useMemo(
    () => buildPlanEconomicsDisplay(plans, featurePricing, t, i18n.language),
    [plans, featurePricing, t, i18n.language],
  )

  const modalSubtitle =
    currentTier === 'STANDARD'
      ? t('upgradeTopBar.modalSubtitleStandard')
      : t('upgradeTopBar.modalSubtitle')

  const upgradeCta =
    currentTier === 'STANDARD'
      ? t('upgradeTopBar.upgradeCtaVip')
      : t('upgradeTopBar.upgradeCta')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('upgradeTopBar.modalTitle')}
      className="max-w-5xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors py-1"
          >
            {t('upgradeTopBar.close')}
          </button>
          <Link
            to={upgradePath}
            onClick={onClose}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#212121] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#333333] transition-colors"
          >
            {upgradeCta}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      }
    >
      <p className="text-sm text-gray-600 mb-4">{modalSubtitle}</p>

      <UpgradePlanCompareTable economics={economics} currentTier={currentTier} />

      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-950 leading-relaxed">
        <p className="font-semibold">{t('upgradeTopBar.valueCallout.title')}</p>
        <p className="mt-1 text-emerald-900 text-xs sm:text-sm">
          {t('upgradeTopBar.valueCallout.body', {
            standardPrice: economics.standardPrice,
            standardPoints: economics.standardPoints,
            vipPrice: economics.vipPrice,
            vipPoints: economics.vipPoints,
            standardOfferCost: economics.standardOfferCost,
            vipOfferCost: economics.vipOfferCost,
            offerPoints: offerPointCost,
          })}
        </p>
      </div>

      <UpgradePlanSocialProof />
    </Modal>
  )
}
